/**
 * Collection Filters and Sort Functionality
 */

class CollectionFilters {
  constructor() {
    // Mobile elements
    this.filterButton = document.getElementById('filter-button');
    this.filterDrawer = document.getElementById('filter-drawer');
    this.filterOverlay = document.getElementById('filter-overlay');
    this.closeFilterButton = document.getElementById('close-filter');
    this.sortButton = document.getElementById('sort-button');
    this.sortDropdown = document.getElementById('sort-dropdown');

    // Desktop sort elements
    this.desktopSortButton = document.getElementById('desktop-sort-button');
    this.desktopSortDropdown = document.getElementById('desktop-sort-dropdown');

    // Desktop elements
    this.desktopFilterForm = document.getElementById('desktop-filter-form');
    this.resetAllButton = document.getElementById('reset-all-filters');

    // Individual filter count and reset elements
    this.availabilityFilterCount = document.getElementById('availability-filter-count');
    this.availabilityResetButton = document.getElementById('availability-reset-filters');
    this.availabilityBadge = document.getElementById('availability-badge');
    this.priceResetButton = document.getElementById('price-reset-filters');
    this.priceBadge = document.getElementById('price-badge');
    this.productTypeFilterCount = document.getElementById('product-type-filter-count');
    this.productTypeResetButton = document.getElementById('product-type-reset-filters');
    this.productTypeBadge = document.getElementById('product-type-badge');
    this.brandFilterCount = document.getElementById('brand-filter-count');
    this.brandResetButton = document.getElementById('brand-reset-filters');
    this.brandBadge = document.getElementById('brand-badge');

    // Desktop dropdown elements
    this.availabilityDropdownButton = document.getElementById('availability-dropdown-button');
    this.availabilityDropdown = document.getElementById('availability-dropdown');
    this.priceDropdownButton = document.getElementById('price-dropdown-button');
    this.priceDropdown = document.getElementById('price-dropdown');
    this.productTypeDropdownButton = document.getElementById('product-type-dropdown-button');
    this.productTypeDropdown = document.getElementById('product-type-dropdown');
    this.brandDropdownButton = document.getElementById('brand-dropdown-button');
    this.brandDropdown = document.getElementById('brand-dropdown');

    // Desktop filter inputs
    this.desktopPriceMin = document.getElementById('desktop-price-min');
    this.desktopPriceMax = document.getElementById('desktop-price-max');
    this.priceApplyButton = document.getElementById('price-apply-button');

    this.init();
  }

  init() {
    // Mobile functionality
    if (this.filterButton) {
      this.filterButton.addEventListener('click', () => this.openFilter());
    }

    if (this.closeFilterButton) {
      this.closeFilterButton.addEventListener('click', () => this.closeFilter());
    }

    if (this.filterOverlay) {
      this.filterOverlay.addEventListener('click', () => this.closeFilter());
    }

    if (this.sortButton) {
      this.sortButton.addEventListener('click', () => this.toggleSort());
    }

    if (this.desktopSortButton) {
      this.desktopSortButton.addEventListener('click', () => this.toggleDesktopSort());
    }

    // Desktop functionality
    this.initDesktopDropdowns();
    this.initDesktopFilters();

    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => this.handleOutsideClick(e));

    // Handle escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeFilter();
        this.closeAllDropdowns();
      }
    });

    // Handle sort dropdown links
    this.initSortLinks();

    // Initialize filter counts
    this.updateAllFilterCounts();

    // Update reset all button visibility
    this.updateResetAllButtonVisibility();

    // Update price apply button state
    this.updatePriceApplyButtonState();
  }

  // Desktop dropdown initialization
  initDesktopDropdowns() {
    const dropdowns = [
      { button: this.availabilityDropdownButton, dropdown: this.availabilityDropdown },
      { button: this.priceDropdownButton, dropdown: this.priceDropdown },
      { button: this.productTypeDropdownButton, dropdown: this.productTypeDropdown },
      { button: this.brandDropdownButton, dropdown: this.brandDropdown },
    ];

    dropdowns.forEach(({ button, dropdown }) => {
      if (button && dropdown) {
        button.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleDesktopDropdown(button, dropdown);
        });
      }
    });
  }

  // Desktop filter initialization
  initDesktopFilters() {
    // Individual reset buttons
    if (this.availabilityResetButton) {
      this.availabilityResetButton.addEventListener('click', () => this.resetAvailabilityFilters());
    }
    if (this.priceResetButton) {
      this.priceResetButton.addEventListener('click', () => this.resetPriceFilters());
    }
    if (this.productTypeResetButton) {
      this.productTypeResetButton.addEventListener('click', () => this.resetProductTypeFilters());
    }
    if (this.brandResetButton) {
      this.brandResetButton.addEventListener('click', () => this.resetBrandFilters());
    }

    // Reset all button
    if (this.resetAllButton) {
      this.resetAllButton.addEventListener('click', () => this.resetAllFilters());
    }

    // Price apply button
    if (this.priceApplyButton) {
      this.priceApplyButton.addEventListener('click', () => this.applyPriceFilter());
    }

    // Price inputs - only update count, don't auto-apply filter
    if (this.desktopPriceMin) {
      this.desktopPriceMin.addEventListener('input', () => {
        this.updatePriceFilterCount();
        this.updatePriceApplyButtonState();
      });
    }
    if (this.desktopPriceMax) {
      this.desktopPriceMax.addEventListener('input', () => {
        this.updatePriceFilterCount();
        this.updatePriceApplyButtonState();
      });
    }

    // Availability checkbox filters
    const availabilityCheckboxes = document.querySelectorAll('.availability-filter');
    availabilityCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        this.updateAvailabilityFilterCount();
        this.handleDesktopFilterChange();
      });
    });

    // Product type checkbox filters
    const productTypeCheckboxes = document.querySelectorAll('.product-type-filter');
    productTypeCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        this.updateProductTypeFilterCount();
        this.handleDesktopFilterChange();
      });
    });

    // Brand checkbox filters
    const brandCheckboxes = document.querySelectorAll('.brand-filter');
    brandCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        this.updateBrandFilterCount();
        this.handleDesktopFilterChange();
      });
    });
  }

  // Handle outside clicks
  handleOutsideClick(e) {
    // Close sort dropdown
    if (!this.sortButton?.contains(e.target) && !this.sortDropdown?.contains(e.target)) {
      this.closeSortDropdown();
    }

    // Close desktop sort dropdown
    if (!this.desktopSortButton?.contains(e.target) && !this.desktopSortDropdown?.contains(e.target)) {
      this.closeDesktopSortDropdown();
    }

    // Close desktop dropdowns
    const dropdowns = [
      { button: this.availabilityDropdownButton, dropdown: this.availabilityDropdown },
      { button: this.priceDropdownButton, dropdown: this.priceDropdown },
      { button: this.productTypeDropdownButton, dropdown: this.productTypeDropdown },
      { button: this.brandDropdownButton, dropdown: this.brandDropdown },
    ];

    dropdowns.forEach(({ button, dropdown }) => {
      if (button && dropdown && !button.contains(e.target) && !dropdown.contains(e.target)) {
        this.closeDesktopDropdown(button, dropdown);
      }
    });
  }

  openFilter() {
    if (this.filterDrawer && this.filterOverlay) {
      this.filterOverlay.classList.remove('wp-hidden');
      this.filterDrawer.classList.remove('wp--translate-x-full');
      document.body.style.overflow = 'hidden';
    }
  }

  closeFilter() {
    if (this.filterDrawer && this.filterOverlay) {
      this.filterDrawer.classList.add('wp--translate-x-full');
      this.filterOverlay.classList.add('wp-hidden');
      document.body.style.overflow = '';
    }
  }

  toggleSort() {
    if (this.sortDropdown) {
      const isHidden = this.sortDropdown.classList.contains('wp-hidden');

      // Close all other dropdowns first
      this.closeAllDesktopDropdowns();
      this.closeDesktopSortDropdown();

      if (isHidden) {
        this.sortDropdown.classList.remove('wp-hidden');
        this.sortButton.setAttribute('aria-expanded', 'true');
      } else {
        this.closeSortDropdown();
      }
    }
  }

  closeSortDropdown() {
    if (this.sortDropdown) {
      this.sortDropdown.classList.add('wp-hidden');
      this.sortButton?.setAttribute('aria-expanded', 'false');
    }
  }

  toggleDesktopSort() {
    if (this.desktopSortDropdown) {
      const isHidden = this.desktopSortDropdown.classList.contains('wp-hidden');

      // Close all dropdowns (including filter dropdowns) first
      this.closeAllDropdowns();

      if (isHidden) {
        this.desktopSortDropdown.classList.remove('wp-hidden');
        this.desktopSortButton.setAttribute('aria-expanded', 'true');
      }
    }
  }

  closeDesktopSortDropdown() {
    if (this.desktopSortDropdown) {
      this.desktopSortDropdown.classList.add('wp-hidden');
      this.desktopSortButton?.setAttribute('aria-expanded', 'false');
    }
  }

  // Desktop dropdown methods
  toggleDesktopDropdown(button, dropdown) {
    const isHidden = dropdown.classList.contains('wp-hidden');

    // Close all dropdowns (including sort) first
    this.closeAllDropdowns();

    if (isHidden) {
      dropdown.classList.remove('wp-hidden');
      button.setAttribute('aria-expanded', 'true');
    }
  }

  closeDesktopDropdown(button, dropdown) {
    dropdown.classList.add('wp-hidden');
    button.setAttribute('aria-expanded', 'false');
  }

  closeAllDesktopDropdowns() {
    const dropdowns = [
      { button: this.availabilityDropdownButton, dropdown: this.availabilityDropdown },
      { button: this.priceDropdownButton, dropdown: this.priceDropdown },
      { button: this.productTypeDropdownButton, dropdown: this.productTypeDropdown },
      { button: this.brandDropdownButton, dropdown: this.brandDropdown },
    ];

    dropdowns.forEach(({ button, dropdown }) => {
      if (button && dropdown) {
        this.closeDesktopDropdown(button, dropdown);
      }
    });
  }

  // Close all dropdowns (including sort)
  closeAllDropdowns() {
    // Close desktop filter dropdowns
    this.closeAllDesktopDropdowns();

    // Close desktop sort dropdown
    this.closeDesktopSortDropdown();

    // Close mobile sort dropdown
    this.closeSortDropdown();
  }

  // Handle desktop filter changes
  handleDesktopFilterChange() {
    // Debounce the filter application
    clearTimeout(this.filterTimeout);
    this.filterTimeout = setTimeout(() => {
      this.applyDesktopFilters();
    }, 500);
  }

  // Apply desktop filters
  applyDesktopFilters() {
    if (!this.desktopFilterForm) return;

    const url = new URL(window.location);

    // Clear existing filter parameters
    const keysToRemove = [];
    for (const [key] of url.searchParams) {
      if (key.startsWith('filter.')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => url.searchParams.delete(key));

    // Add price filters
    if (this.desktopPriceMin && this.desktopPriceMin.value) {
      url.searchParams.set('filter.v.price.gte', this.desktopPriceMin.value);
    }
    if (this.desktopPriceMax && this.desktopPriceMax.value) {
      url.searchParams.set('filter.v.price.lte', this.desktopPriceMax.value);
    }

    // Add checkbox filters
    const availabilityFilters = document.querySelectorAll('.availability-filter:checked');
    const productTypeFilters = document.querySelectorAll('.product-type-filter:checked');
    const brandFilters = document.querySelectorAll('.brand-filter:checked');

    availabilityFilters.forEach((filter) => {
      url.searchParams.append('filter.v.availability', filter.value);
    });

    productTypeFilters.forEach((filter) => {
      url.searchParams.append('filter.p.product_type', filter.value);
    });

    brandFilters.forEach((filter) => {
      url.searchParams.append('filter.p.vendor', filter.value);
    });
    url.searchParams.delete('page');

    this.navigate(url.toString());
  }

  // Individual reset methods
  resetAvailabilityFilters() {
    const checkboxes = document.querySelectorAll('.availability-filter');
    checkboxes.forEach((checkbox) => (checkbox.checked = false));
    this.updateAvailabilityFilterCount();
    this.handleDesktopFilterChange();
  }

  resetPriceFilters() {
    if (this.desktopPriceMin) this.desktopPriceMin.value = '';
    if (this.desktopPriceMax) this.desktopPriceMax.value = '';
    this.updatePriceFilterCount();
    this.updatePriceApplyButtonState();
    // Apply the reset immediately
    this.handleDesktopFilterChange();
  }

  // Apply price filter when Apply button is clicked
  applyPriceFilter() {
    // Show loading state
    if (this.priceApplyButton) {
      this.priceApplyButton.textContent = 'Applying...';
      this.priceApplyButton.disabled = true;
    }

    this.handleDesktopFilterChange();

    // Close the price dropdown after applying
    if (this.priceDropdown && this.priceDropdownButton) {
      this.closeDesktopDropdown(this.priceDropdownButton, this.priceDropdown);
    }
  }

  // Update price apply button state based on input changes
  updatePriceApplyButtonState() {
    if (!this.priceApplyButton) return;

    // Get current input values
    const currentMinValue = this.desktopPriceMin ? this.desktopPriceMin.value.trim() : '';
    const currentMaxValue = this.desktopPriceMax ? this.desktopPriceMax.value.trim() : '';

    // Get applied values from URL
    const urlParams = new URLSearchParams(window.location.search);
    const appliedMinValue = urlParams.get('filter.v.price.gte') || '';
    const appliedMaxValue = urlParams.get('filter.v.price.lte') || '';

    // Check if current values are different from applied values
    const hasChanges = currentMinValue !== appliedMinValue || currentMaxValue !== appliedMaxValue;

    // Update button appearance based on whether there are pending changes
    if (hasChanges) {
      this.priceApplyButton.disabled = false;
      this.priceApplyButton.classList.remove('wp-bg-gray-100', 'wp-text-gray-700');
      this.priceApplyButton.classList.add('wp-bg-primary', 'wp-text-white');
      this.priceApplyButton.textContent = 'Apply';
    } else {
      this.priceApplyButton.disabled = true;
      this.priceApplyButton.classList.remove('wp-bg-primary', 'wp-text-white');
      this.priceApplyButton.classList.add('wp-bg-gray-100', 'wp-text-gray-700');
      this.priceApplyButton.textContent = 'Applied';
    }
  }

  resetProductTypeFilters() {
    const checkboxes = document.querySelectorAll('.product-type-filter');
    checkboxes.forEach((checkbox) => (checkbox.checked = false));
    this.updateProductTypeFilterCount();
    this.handleDesktopFilterChange();
  }

  resetBrandFilters() {
    const checkboxes = document.querySelectorAll('.brand-filter');
    checkboxes.forEach((checkbox) => (checkbox.checked = false));
    this.updateBrandFilterCount();
    this.handleDesktopFilterChange();
  }

  // Reset all filters
  resetAllFilters() {
    // Reset all individual filters
    this.resetAvailabilityFilters();
    this.resetPriceFilters();
    this.resetProductTypeFilters();
    this.resetBrandFilters();

    // Navigate to collection URL without any filters
    const url = new URL(window.location.href);

    // Remove all filter parameters
    const keysToRemove = [];
    for (const [key] of url.searchParams) {
      if (key.startsWith('filter.')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => url.searchParams.delete(key));
    url.searchParams.delete('page');

    this.navigate(url.toString());
  }

  // Update reset all button visibility
  updateResetAllButtonVisibility() {
    if (!this.resetAllButton) return;

    // Check if any filters are applied
    const hasFilters = this.hasActiveFilters();

    if (hasFilters) {
      this.resetAllButton.classList.remove('wp-hidden');
    } else {
      this.resetAllButton.classList.add('wp-hidden');
    }
  }

  // Check if any filters are currently active
  hasActiveFilters() {
    // Check URL parameters for any filter parameters
    const urlParams = new URLSearchParams(window.location.search);
    for (const [key] of urlParams) {
      if (key.startsWith('filter.')) {
        return true;
      }
    }
    return false;
  }

  // Individual filter count updates
  updateAvailabilityFilterCount() {
    if (!this.availabilityFilterCount) return;
    const checkedFilters = document.querySelectorAll('.availability-filter:checked');
    const count = checkedFilters.length;
    this.availabilityFilterCount.textContent = `${count} selected`;

    // Update badge
    if (this.availabilityBadge) {
      if (count > 0) {
        this.availabilityBadge.classList.remove('wp-hidden');
      } else {
        this.availabilityBadge.classList.add('wp-hidden');
      }
    }

    // Update reset all button visibility
    this.updateResetAllButtonVisibility();
  }

  updatePriceFilterCount() {
    // Check if price filters are applied in URL (not just input values)
    const urlParams = new URLSearchParams(window.location.search);
    const hasMinPrice = urlParams.has('filter.v.price.gte');
    const hasMaxPrice = urlParams.has('filter.v.price.lte');
    const hasAppliedPrice = hasMinPrice || hasMaxPrice;

    // Update badge based on applied filters, not input values
    if (this.priceBadge) {
      if (hasAppliedPrice) {
        this.priceBadge.classList.remove('wp-hidden');
      } else {
        this.priceBadge.classList.add('wp-hidden');
      }
    }

    // Update reset all button visibility
    this.updateResetAllButtonVisibility();
  }

  updateProductTypeFilterCount() {
    if (!this.productTypeFilterCount) return;
    const checkedFilters = document.querySelectorAll('.product-type-filter:checked');
    const count = checkedFilters.length;
    this.productTypeFilterCount.textContent = `${count} selected`;

    // Update badge
    if (this.productTypeBadge) {
      if (count > 0) {
        this.productTypeBadge.classList.remove('wp-hidden');
      } else {
        this.productTypeBadge.classList.add('wp-hidden');
      }
    }

    // Update reset all button visibility
    this.updateResetAllButtonVisibility();
  }

  updateBrandFilterCount() {
    if (!this.brandFilterCount) return;
    const checkedFilters = document.querySelectorAll('.brand-filter:checked');
    const count = checkedFilters.length;
    this.brandFilterCount.textContent = `${count} selected`;

    // Update badge
    if (this.brandBadge) {
      if (count > 0) {
        this.brandBadge.classList.remove('wp-hidden');
      } else {
        this.brandBadge.classList.add('wp-hidden');
      }
    }

    // Update reset all button visibility
    this.updateResetAllButtonVisibility();
  }

  // Update all filter counts
  updateAllFilterCounts() {
    this.updateAvailabilityFilterCount();
    this.updatePriceFilterCount();
    this.updateProductTypeFilterCount();
    this.updateBrandFilterCount();
    this.updateResetAllButtonVisibility();
  }

  initSortLinks() {
    const bindSortLinks = (links) => {
      if (!links) return;
      links.forEach((link) => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          const url = new URL(window.location);
          const sortBy = new URL(link.href).searchParams.get('sort_by');

          if (sortBy) {
            url.searchParams.set('sort_by', sortBy);
          } else {
            url.searchParams.delete('sort_by');
          }

          url.searchParams.delete('page');

          this.closeAllDropdowns();
          this.navigate(url.toString());
        });
      });
    };

    bindSortLinks(this.sortDropdown?.querySelectorAll('a'));
    bindSortLinks(this.desktopSortDropdown?.querySelectorAll('a'));
  }

  navigate(href) {
    if (window.collectionAjax && typeof window.collectionAjax.load === 'function') {
      window.collectionAjax.load(href);
    } else {
      window.location.href = href;
    }
  }

  // Update current sort display
  updateSortDisplay() {
    const urlParams = new URLSearchParams(window.location.search);
    const currentSort = urlParams.get('sort_by');

    const sortLabels = {
      manual: 'Trending',
      'best-selling': 'Best Selling',
      'title-ascending': 'A-Z',
      'title-descending': 'Z-A',
      'price-ascending': 'Price: Low to High',
      'price-descending': 'Price: High to Low',
      'created-ascending': 'Date: Old to New',
      'created-descending': 'Date: New to Old',
    };

    // Update mobile sort button
    const sortButton = this.sortButton?.querySelector('span');
    if (sortButton && currentSort) {
      sortButton.textContent = sortLabels[currentSort] || 'Sort';
    }

    // Update desktop sort button
    const desktopSortButton = this.desktopSortButton?.querySelector('span');
    if (desktopSortButton && currentSort) {
      desktopSortButton.textContent = sortLabels[currentSort] || 'Sort';
    }
  }

  // Initialize desktop filters from URL parameters
  initializeDesktopFiltersFromURL() {
    const urlParams = new URLSearchParams(window.location.search);

    // Set price values
    if (this.desktopPriceMin) {
      this.desktopPriceMin.value = urlParams.get('filter.v.price.gte') || '';
    }
    if (this.desktopPriceMax) {
      this.desktopPriceMax.value = urlParams.get('filter.v.price.lte') || '';
    }

    // Set checkbox values
    const availabilityValues = urlParams.getAll('filter.v.availability');
    const productTypeValues = urlParams.getAll('filter.p.product_type');
    const vendorValues = urlParams.getAll('filter.p.vendor');

    document.querySelectorAll('.availability-filter').forEach((checkbox) => {
      checkbox.checked = availabilityValues.includes(checkbox.value);
    });

    document.querySelectorAll('.product-type-filter').forEach((checkbox) => {
      checkbox.checked = productTypeValues.includes(checkbox.value);
    });

    document.querySelectorAll('.brand-filter').forEach((checkbox) => {
      checkbox.checked = vendorValues.includes(checkbox.value);
    });

    this.updateAllFilterCounts();
  }
}

class CollectionAjax {
  constructor() {
    this.section = document.querySelector('[data-collection-section]');
    if (!this.section) return;

    this.sectionId = this.section.dataset.sectionId;
    this.gridContainer = this.section.querySelector('[data-collection-grid]');
    this.skeletonGridTemplate = document.getElementById('collection-skeleton-grid-template');
    this.skeletonCardTemplate = document.getElementById('collection-skeleton-card-template');
    this.productsPerPage = parseInt(this.section.dataset.productsPerPage, 10) || 24;
    this.abortController = null;
    this.isLoading = false;

    this.bindPaginationLinks();
    this.bindImageFadeIn(this.section);

    window.addEventListener('popstate', () => {
      this.load(window.location.href, { push: false });
    });
  }

  bindPaginationLinks() {
    if (!this.gridContainer) return;
    this.gridContainer.addEventListener('click', (e) => {
      const link = e.target.closest('a[href]');
      if (!link) return;
      const url = new URL(link.href, window.location.origin);
      if (url.pathname !== window.location.pathname) return;
      if (!url.searchParams.has('page')) return;

      e.preventDefault();
      this.scrollToTop();
      this.load(url.toString());
    });
  }

  buildSkeletonGrid() {
    if (!this.skeletonGridTemplate || !this.skeletonCardTemplate) return null;

    const gridFragment = this.skeletonGridTemplate.content.cloneNode(true);
    const grid = gridFragment.querySelector('[data-collection-skeleton-grid]');
    if (!grid) return null;

    for (let i = 0; i < this.productsPerPage; i += 1) {
      grid.appendChild(this.skeletonCardTemplate.content.cloneNode(true));
    }
    return grid;
  }

  showSkeleton() {
    if (!this.gridContainer) return;
    this.gridContainer.classList.add('is-loading');

    const existingGrid = this.gridContainer.querySelector('[data-collection-grid-items]');
    const skeletonGrid = this.buildSkeletonGrid();
    if (existingGrid && skeletonGrid) {
      existingGrid.replaceWith(skeletonGrid);
    }
  }

  async load(href, { push = true } = {}) {
    if (!this.gridContainer || !this.sectionId) {
      window.location.href = href;
      return;
    }

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    this.isLoading = true;

    this.showSkeleton();

    const fetchUrl = new URL(href, window.location.origin);
    fetchUrl.searchParams.set('section_id', this.sectionId);

    try {
      const response = await fetch(fetchUrl.toString(), {
        signal: this.abortController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newGrid = doc.querySelector('[data-collection-grid]');
      if (!newGrid) throw new Error('Grid not found in response');

      this.swapGrid(newGrid);
      this.swapBreadcrumb(doc);

      if (push) {
        window.history.pushState({ collectionAjax: true }, '', href);
      }

      this.reinitFilters();
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Collection AJAX failed, falling back to reload:', err);
      window.location.href = href;
    } finally {
      this.isLoading = false;
    }
  }

  swapGrid(newGrid) {
    newGrid.classList.add('wp-relative');
    this.gridContainer.replaceWith(newGrid);
    this.gridContainer = newGrid;
    this.gridContainer.classList.remove('is-loading');

    this.bindPaginationLinks();
    this.bindImageFadeIn(this.gridContainer);
    this.animateItemsIn();
  }

  swapBreadcrumb(doc) {
    const current = this.section.querySelector('[data-collection-breadcrumb]');
    const next = doc.querySelector('[data-collection-breadcrumb]');
    if (current && next) {
      current.innerHTML = next.innerHTML;
    }
  }

  animateItemsIn() {
    const items = this.gridContainer.querySelectorAll('[data-collection-grid-items] > *');
    items.forEach((item, index) => {
      item.classList.add('collection-grid-enter');
      const delay = Math.min(index * 25, 250);
      requestAnimationFrame(() => {
        setTimeout(() => {
          item.classList.add('collection-grid-enter-active');
          item.classList.remove('collection-grid-enter');
        }, delay);
      });
    });
  }

  bindImageFadeIn(scope) {
    const images = scope.querySelectorAll('.collection-card-image');
    images.forEach((img) => {
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('is-loaded');
        return;
      }
      img.classList.add('collection-card-image--fade');
      const reveal = () => img.classList.add('is-loaded');
      img.addEventListener('load', reveal, { once: true });
      img.addEventListener('error', reveal, { once: true });
    });
  }

  scrollToTop() {
    const top = this.section.getBoundingClientRect().top + window.pageYOffset - 16;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  }

  reinitFilters() {
    if (!window.collectionFilters) return;
    window.collectionFilters.updateSortDisplay();
    window.collectionFilters.initializeDesktopFiltersFromURL();
    window.collectionFilters.updateAllFilterCounts();
    window.collectionFilters.updateResetAllButtonVisibility();
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const filters = new CollectionFilters();
  window.collectionFilters = filters;
  filters.updateSortDisplay();
  filters.initializeDesktopFiltersFromURL();
  filters.updateAllFilterCounts();

  window.collectionAjax = new CollectionAjax();
});

// Handle form submission with loading state
document.addEventListener('DOMContentLoaded', () => {
  const filterForm = document.querySelector('#filter-drawer form');
  if (filterForm) {
    filterForm.addEventListener('submit', (e) => {
      const submitButton = filterForm.querySelector('button[type="submit"]');
      if (submitButton) {
        submitButton.textContent = 'Applying...';
        submitButton.disabled = true;
      }
    });
  }
});

// Auto-close filter drawer on successful filter application
window.addEventListener('pageshow', () => {
  const filterDrawer = document.getElementById('filter-drawer');
  const filterOverlay = document.getElementById('filter-overlay');

  if (filterDrawer && filterOverlay) {
    filterDrawer.classList.add('wp--translate-x-full');
    filterOverlay.classList.add('wp-hidden');
    document.body.style.overflow = '';
  }
});
